import { useEffect, useState } from "react";
import { ArrowRight, Activity, ShieldCheck, Layers, Heart } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const HERO_VIDEO = `${process.env.PUBLIC_URL}/videos/hero.mp4`;

export default function Hero() {
  const ref = useReveal();
  const [time, setTime] = useState("");

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const s = now.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Kolkata",
      });
      setTime(`${s} IST · Madurai`);
    };
    tick();
    const i = setInterval(tick, 30_000);
    return () => clearInterval(i);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Background video */}
      <div className="absolute inset-0 z-0">
        <video
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        {/* Left side white for text readability, fades to transparent so video shows on right */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#F8F9FA] via-[#F8F9FA]/60 to-transparent" />
      </div>

      {/* Top brand strip */}
      <div className="relative z-10 pt-28 md:pt-32 px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.28em] text-[#5F6368]">
          <span data-testid="hero-tag">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D62828] mr-2 align-middle animate-pulse-soft" />
            Healthcare Technology · India
          </span>
          <span className="hidden md:inline" data-testid="hero-time">
            {time}
          </span>
        </div>
      </div>

      {/* Main grid \u2014 text LEFT, cards RIGHT (original layout) */}
      <div className="relative z-10 px-6 md:px-12 lg:px-24 pt-12 md:pt-20 pb-28 md:pb-40">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* Headline \u2014 LEFT */}
          <div className="col-span-12 lg:col-span-8 reveal">
            <h1
              data-testid="hero-headline"
              className="font-display text-[#111111] text-[clamp(3.25rem,9vw,9.5rem)] leading-[0.88] tracking-[-0.045em]"
            >
              Healthcare,
              <br />
              <span className="font-serif-italic text-[#D62828] italic">
                reimagined
              </span>
              <br />
              through technology.
            </h1>

            <p
              data-testid="hero-subhead"
              className="mt-10 max-w-xl text-[15px] md:text-[17px] leading-[1.65] text-[#5F6368]"
            >
              Blude TechX builds evidence-based digital health solutions
              connecting patients, professionals, and healthcare systems through
              technology designed for real-world impact.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("build")}
                className="group inline-flex items-center gap-3 rounded-full bg-[#111111] text-white pl-7 pr-2 py-2 text-[14px] font-semibold tracking-wide hover:bg-[#D62828] transition-colors duration-500"
              >
                Explore Our Work
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white text-[#111111] group-hover:translate-x-1 transition-transform duration-500">
                  <ArrowRight size={16} />
                </span>
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("who")}
                className="inline-flex items-center gap-2 rounded-full border border-[#CED4DA] bg-white/70 backdrop-blur-md text-[#111111] px-6 py-3.5 text-[14px] font-semibold tracking-wide hover:bg-white transition-colors"
              >
                Our Mission
              </button>
            </div>

            {/* Trust strip */}
            <div className="mt-16 grid grid-cols-3 max-w-lg gap-8 reveal">
              <div>
                <div className="font-display text-3xl text-[#111111] tracking-tight">03</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#5F6368] mt-1">Products</div>
              </div>
              <div>
                <div className="font-display text-3xl text-[#111111] tracking-tight">100<span className="text-[#D62828]">%</span></div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#5F6368] mt-1">Evidence-led</div>
              </div>
              <div>
                <div className="font-display text-3xl text-[#111111] tracking-tight">India</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#5F6368] mt-1">Region One</div>
              </div>
            </div>
          </div>

          {/* Floating intelligence cards \u2014 RIGHT */}
          <div className="col-span-12 lg:col-span-4 relative min-h-[400px] hidden lg:block">
            <FloatingCard
              testid="float-card-reach"
              className="absolute right-0 top-2 animate-float-slow"
              icon={<Activity size={16} />}
              label="Platform Reach"
              value="Patients · Doctors · Hospitals"
              tag="Network"
            />
            <FloatingCard
              testid="float-card-digital"
              className="absolute right-12 top-44 animate-float-mid"
              icon={<Heart size={16} />}
              label="Digital Health"
              value="Care, validated by science."
              tag="Pillar"
              dark
            />
            <FloatingCard
              testid="float-card-pipeline"
              className="absolute right-2 top-[22rem] animate-float-slow"
              icon={<Layers size={16} />}
              label="Product Pipeline"
              value="PIRAVI · INSURE BLU · ZIMLAB"
              tag="2025"
            />
            <div className="absolute left-0 bottom-2 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[#5F6368]">
              <ShieldCheck size={14} className="text-[#D62828]" />
              Privacy-first by design
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute z-10 bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#5F6368]">
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="block h-10 w-px bg-gradient-to-b from-[#5F6368] to-transparent" />
      </div>
    </section>
  );
}

function FloatingCard({ icon, label, value, tag, dark, className, testid }) {
  return (
    <div
      data-testid={testid}
      className={`${className} w-[280px] rounded-2xl border ${
        dark
          ? "bg-[#0F1B2D] text-white border-white/10 shadow-2xl"
          : "bg-white/85 backdrop-blur-xl text-[#111111] border-[#CED4DA] shadow-[0_18px_60px_-25px_rgba(15,27,45,0.35)]"
      } p-5`}
    >
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.24em] opacity-80">
        <span className="inline-flex items-center gap-2">
          <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${dark ? "bg-white/10" : "bg-[#F8F9FA]"}`}>
            {icon}
          </span>
          {label}
        </span>
        <span className={dark ? "text-white/60" : "text-[#5F6368]"}>{tag}</span>
      </div>
      <div className="mt-5 font-display text-[18px] tracking-tight leading-snug">{value}</div>
      <div className={`mt-5 h-px ${dark ? "bg-white/15" : "bg-[#CED4DA]"}`} />
      <div className="mt-3 flex items-center justify-between text-[11px]">
        <span className={dark ? "text-white/60" : "text-[#5F6368]"}>Live signal</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#D62828] animate-pulse-soft" />
          <span className={dark ? "text-white/80" : "text-[#111111]"}>OK</span>
        </span>
      </div>
    </div>
  );
}
